import { z } from "zod";
import { ActorType } from "@common/types/auditLog";
import { AgentRunState } from "@common/types/agentRun";
import { agentRunProposalToolSchema } from "./agent-run.schema";

export const TASK_ACTIVITY_EVENT_TYPES = [
    "task_created",
    "task_updated",
    "task_completed",
    "run_started",
    "stage_completed",
    "proposals_posted",
    "proposal_applied",
    "waiting",
    "run_paused",
    "run_continued",
    "run_stopped",
    "run_finished",
] as const;
export type TaskActivityEventType = (typeof TASK_ACTIVITY_EVENT_TYPES)[number];

export const activityScopeSchema = z.object({
    taskId: z.string().uuid(),
    agentRunId: z.string().uuid().nullable(),
});
export type ActivityScope = z.infer<typeof activityScopeSchema>;

const baseSchema = z.object({
    id: z.string().uuid(),
    workspaceId: z.string().uuid(),
    activityScope: activityScopeSchema,
    actorType: z.nativeEnum(ActorType),
    actorId: z.number().int().nullable(),
    dedupeKey: z.string().nullable(),
    createdAt: z.string().datetime({ offset: true }),
});

const empty = z.object({});

export const taskActivityPayloadSchema = z.discriminatedUnion("eventType", [
    z.object({ eventType: z.literal("task_created"), metadata: empty }),
    z.object({ eventType: z.literal("task_updated"), metadata: empty }),
    z.object({ eventType: z.literal("task_completed"), metadata: empty }),
    z.object({ eventType: z.literal("run_started"), metadata: empty }),
    z.object({
        eventType: z.literal("stage_completed"),
        metadata: z.object({ stageName: z.string() }),
    }),
    z.object({
        eventType: z.literal("proposals_posted"),
        metadata: z.object({ count: z.number().int(), stageName: z.string() }),
    }),
    z.object({
        eventType: z.literal("proposal_applied"),
        metadata: z.object({
            tool: agentRunProposalToolSchema,
            operation: z.string().nullable(),
            count: z.number().int().nullable(),
            isCategoryChange: z.boolean(),
        }),
    }),
    z.object({
        eventType: z.literal("waiting"),
        metadata: z.object({
            waitedOnTaskId: z.string().uuid(),
            waitedOnTitle: z.string().nullable(),
        }),
    }),
    z.object({ eventType: z.literal("run_paused"), metadata: empty }),
    z.object({ eventType: z.literal("run_continued"), metadata: empty }),
    z.object({ eventType: z.literal("run_stopped"), metadata: empty }),
    z.object({
        eventType: z.literal("run_finished"),
        metadata: z.object({
            state: z.nativeEnum(AgentRunState),
            finishReason: z.string().nullable(),
        }),
    }),
]);
export type TaskActivityPayload = z.infer<typeof taskActivityPayloadSchema>;

export const taskActivityEventSchema = z.intersection(
    baseSchema,
    taskActivityPayloadSchema,
);
export type TaskActivityEvent = z.infer<typeof taskActivityEventSchema>;

export const taskActivityPathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
    taskId: z.string().uuid(),
});
export const taskActivityListQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const taskActivityListResponseSchema = z.object({
    task: z.object({ id: z.string().uuid(), title: z.string() }),
    events: z.array(taskActivityEventSchema),
});
export type TaskActivityListResponse = z.infer<
    typeof taskActivityListResponseSchema
>;

export const agentRunInternalActivityBodySchema = z.intersection(
    z.object({ dedupeKey: z.string().nullable() }),
    taskActivityPayloadSchema,
);
export type AgentRunInternalActivityBody = z.infer<
    typeof agentRunInternalActivityBodySchema
>;
